import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Flame, Star, UtensilsCrossed } from "lucide-react";
import { MENU_CATEGORIES, MenuCategory, ROUTE_PATHS } from "@/lib/index";
import { menuItems, locationInfo } from "@/data/index";
import { MenuCard } from "@/components/Cards";
import { Button } from "@/components/ui/button";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";

export default function Menu() {
  const [activeCategory, setActiveCategory] = useState<MenuCategory | "All">("All");

  const filteredItems = useMemo(() => {
    if (activeCategory === "All") return menuItems;
    return menuItems.filter((item) => item.category === activeCategory);
  }, [activeCategory]);

  return (
    <div className="min-h-screen pb-24">
      {/* Hero Section */}
      <section className="relative py-20 bg-accent/30 overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-10 left-10 w-64 h-64 rounded-full bg-primary/20 blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-6">
              <UtensilsCrossed className="w-4 h-4" />
              Authentic Korean Cuisine
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
              Our <span className="text-primary">Menu</span>
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              From our signature sizzling Dakgalbi to the crunchiest Korean Fried Chicken,
              every dish is prepared with authentic ingredients and passion.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Navigation */}
      <nav className="sticky top-16 z-40 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-4 overflow-x-auto py-4 no-scrollbar">
            <Button
              variant={activeCategory === "All" ? "default" : "ghost"}
              onClick={() => setActiveCategory("All")}
              className="rounded-full whitespace-nowrap"
            >
              All Items
            </Button>
            {MENU_CATEGORIES.map((category) => (
              <Button
                key={category}
                variant={activeCategory === category ? "default" : "ghost"}
                onClick={() => setActiveCategory(category)}
                className="rounded-full whitespace-nowrap"
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </nav>

      {/* Menu Grid */}
      <main className="container mx-auto px-4 mt-12">
        <motion.div
          layout
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          <AnimatePresence mode="popLayout">
            {filteredItems.map((item) => (
              <motion.div
                key={item.id}
                layout
                variants={staggerItem}
                initial="hidden"
                animate="visible"
                exit={{ opacity: 0, scale: 0.9 }}
                transition={springPresets.snappy}
              >
                <MenuCard item={item} />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Special Tags Legend */}
        <motion.div 
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-16 p-8 rounded-3xl bg-muted/50 border border-border flex flex-wrap justify-center gap-8 text-sm font-medium"
        >
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-destructive/10 flex items-center justify-center text-destructive">
              <Flame className="w-4 h-4" />
            </div>
            <span>Spicy Flavor</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <Star className="w-4 h-4" />
            </div>
            <span>Bestseller</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-secondary-foreground">
              <ShoppingBag className="w-4 h-4" />
            </div>
            <span>Grab Delivery Available</span>
          </div>
        </motion.div>

        {/* Grab CTA */}
        <motion.section
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-24 p-12 rounded-[2.5rem] bg-primary text-primary-foreground text-center relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-20 -mt-20 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-black/10 rounded-full -ml-20 -mb-20 blur-3xl" />
          
          <div className="relative z-10 max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Craving for Korean Food?</h2>
            <p className="text-primary-foreground/90 mb-8 text-lg">
              Don't feel like driving to Sunway Geo Mall? Get our authentic Dakgalbi delivered straight to your doorstep via GrabFood.
            </p>
            <Button
              asChild
              size="lg"
              variant="secondary"
              className="rounded-full px-8 h-14 text-lg font-bold shadow-xl hover:shadow-primary/20"
            >
              <a href={locationInfo.grabLink} target="_blank" rel="noopener noreferrer">
                <ShoppingBag className="w-5 h-5 mr-2" />
                Order Now on Grab
              </a>
            </Button>
          </div>
        </motion.section>
      </main>
    </div>
  );
}
